/**
 * Request Access Page
 *
 * Public page for prospects to submit registration requests.
 * After submission, platform admins will review and approve/reject.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createRegistrationRequestSchema, type CreateRegistrationRequestValidation } from '../../../lib/validators/registration-request';
import { registrationRequestService } from '../../../api/registration-request.service';
import { getErrorMessage } from '../../../lib/utils/errors';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { CheckCircleIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

export const RequestAccessPage: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateRegistrationRequestValidation>({
    resolver: zodResolver(createRegistrationRequestSchema),
    defaultValues: {
      companyName: '',
      contactName: '',
      contactEmail: '',
      useCase: '',
    },
  });

  const onSubmit = async (data: CreateRegistrationRequestValidation) => {
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      await registrationRequestService.submitRequest(data);
      setSubmittedEmail(data.contactEmail);
      setShowSuccess(true);
    } catch (error) {
      const message = getErrorMessage(error);
      if (message.toLowerCase().includes('already') || message.toLowerCase().includes('exists')) {
        setErrorMessage('A registration request with this email already exists. Please check your email for updates or contact support.');
      } else {
        setErrorMessage(message || 'Failed to submit registration request. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-accent-100">
              <CheckCircleIcon className="h-10 w-10 text-accent-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
              Request Submitted!
            </h2>
            <div className="mt-4 space-y-4">
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Thank you for your interest in Ketrics!
              </p>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                We've received your registration request and will review it shortly.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-800">
                <p className="font-medium mb-2">What happens next:</p>
                <ol className="list-decimal list-inside space-y-1 text-left">
                  <li>Our team will review your request</li>
                  <li>You'll receive an email at <strong>{submittedEmail}</strong></li>
                  <li>If approved, the email will contain a link to complete your registration</li>
                  <li>You can then set up your organization and start using Ketrics</li>
                </ol>
              </div>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                This usually takes 1-2 business days.
              </p>
            </div>
          </div>

          <div className="text-center space-y-2">
            <Button
              variant="primary"
              as={Link}
              to="/login"
              fullWidth
            >
              Go to Login
            </Button>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <div className="max-w-lg w-full space-y-8">
        {/* Header */}
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            Request Access to Ketrics
          </h2>
          <p className="mt-2 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Fill out the form below and our team will review your request.
          </p>
          <p className="mt-1 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
              Sign in
            </Link>
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {errorMessage && (
            <Alert variant="error" dismissible onDismiss={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}

          <div className="card shadow-card rounded-lg p-6 space-y-6">
            {/* Company Name */}
            <FormField
              label="Company Name"
              htmlFor="companyName"
              error={errors.companyName?.message}
              required
            >
              <Input
                id="companyName"
                type="text"
                placeholder="Acme Corporation"
                error={!!errors.companyName}
                {...register('companyName')}
              />
            </FormField>

            {/* Contact Name */}
            <FormField
              label="Your Name"
              htmlFor="contactName"
              error={errors.contactName?.message}
              required
            >
              <Input
                id="contactName"
                type="text"
                placeholder="John Doe"
                error={!!errors.contactName}
                {...register('contactName')}
              />
            </FormField>

            {/* Contact Email */}
            <FormField
              label="Email Address"
              htmlFor="contactEmail"
              error={errors.contactEmail?.message}
              required
            >
              <Input
                id="contactEmail"
                type="email"
                placeholder="john@acme.com"
                error={!!errors.contactEmail}
                {...register('contactEmail')}
              />
            </FormField>

            {/* Use Case */}
            <FormField
              label="How do you plan to use Ketrics?"
              htmlFor="useCase"
              error={errors.useCase?.message}
              hint="Optional - helps us understand your needs better"
            >
              <textarea
                id="useCase"
                rows={3}
                placeholder="Tell us about your use case..."
                className={`
                  block w-full rounded-md border-0 py-1.5 px-3
                  shadow-sm ring-1 ring-inset
                  focus:ring-2 focus:ring-inset focus:ring-primary-600
                  sm:text-sm sm:leading-6
                  ${errors.useCase ? 'ring-red-300 focus:ring-red-500' : ''}
                `}
                style={{ color: 'var(--color-text-primary)', ringColor: errors.useCase ? undefined : 'var(--color-border)' }}
                {...register('useCase')}
              />
            </FormField>
          </div>

          <Button
            type="submit"
            variant="primary"
            fullWidth
            size="lg"
            loading={isSubmitting}
            disabled={isSubmitting}
            icon={<PaperAirplaneIcon className="h-5 w-5" />}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>

          <p className="text-xs text-center" style={{ color: 'var(--color-text-muted)' }}>
            By submitting this form, you agree to our{' '}
            <a href="#" className="text-primary-600 hover:text-primary-500">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="#" className="text-primary-600 hover:text-primary-500">
              Privacy Policy
            </a>
            .
          </p>
        </form>
      </div>
    </div>
  );
};

export default RequestAccessPage;
